import React, { ChangeEvent, useState, useCallback, useEffect } from 'react';
import { Button } from 'antd';
import { OrderCloseFiltersInput } from '../graphql/queries/getPendingCloseOrders';
import { OrderType } from '../../../graphql/types/OrderType';
import { t } from '../../../i18n';
import InputFilterItem from '../../delivery/components/InputFilterItem';
import BatchInputFilterItem from '../../delivery/components/BatchInputFilterItem';
import ParcelFilterStyle from '../../delivery/components/ParcelFilterStyle';
import DateTimeRangeFilterItem from '../../delivery/components/DateTimeRangeFilterItem';
import NumberRangeFilterItem from '../../delivery/components/NumberRangeFilterItem';
import { OrderCloseClientSelectFilter } from './OrderCloseClientSelectFilter';
import { MultiParcelStatusSelectFilter } from '../../../components/filters/MultiParcelStatusSelectFilter';
import { DspSelectFilter } from '../../../components/filters/DspSelectFilter';
import { HubSelectFilter } from '../../../components/filters/HubSelectFilter';
import { WarehouseSelectFilter } from '../../../components/filters/WarehouseSelectFilter';
import { MultiPickupHubSelectFilter } from '../../../components/filters/MultiPickupHubSelectFilter';
import { useQuery } from '@apollo/client';
import {
    ExceptionDictionaryResponse,
    GET_ALL_EXCEPTION
} from '../../delivery/parcels/graphql/queries/getAllException';
import {
    GET_NEXT_ACTION_LIST,
    NextActionListResponse
} from '../../../graphql/queries/getNextActionList';
import { useStatusInternationalization } from '../../delivery/parcels/hooks/useStatusInternationalization';
import {
    hasExceptionOptions,
    hasCloseRecordOptions,
    isComplaintOptions,
    podReviewStatusOptions
} from '../../../config/filterOptions';
import { useDspOptions } from '../../../hooks/useDspOptions';
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../graphql/queries/getChannelAll';

interface OrderCloseFilterProps {
    filters: OrderCloseFiltersInput;
    setFilters: (filters: OrderCloseFiltersInput | ((prev: OrderCloseFiltersInput) => OrderCloseFiltersInput)) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    selectedRows: OrderType[];
}

const OrderCloseFilter: React.FC<OrderCloseFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
    selectedRows
}) => {
    const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const { data: nextActionList } = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST);
    const { data: channelData } = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const { dspOptions } = useDspOptions();
    const { getExceptionReasonText, getNextActionTextFromBackend, exceptionOrderStatusOptions } = useStatusInternationalization();

    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const nextActionSelectData = nextActionList?.getNextActionList || [];
    const [selectedHubId, setSelectedHubId] = useState<string | undefined>(undefined);

    // 监听重置操作：当第一分拣码被清空时，清空 selectedHubId
    useEffect(() => {
        if (!filters.sortingCodeSegment1 && selectedHubId !== undefined) {
            setSelectedHubId(undefined);
        }
    }, [filters.sortingCodeSegment1, selectedHubId]);

    // 当 filters 被重置为空对象时，清空所有本地状态
    useEffect(() => {
        const isEmpty = Object.keys(filters).length === 0;
        if (isEmpty) {
            setSelectedHubId(undefined);
        }
    }, [filters]);

    // 当 hub 被清空时，清空 warehouse（保留作为兜底）
    useEffect(() => {
        if (!filters.hubId && (filters.warehouseId || filters.sortingCodeSegment2)) {
            setFilters(prev => ({ ...prev, warehouseId: undefined, sortingCodeSegment2: undefined }));
        }
    }, [filters.hubId, filters.warehouseId, filters.sortingCodeSegment2, setFilters]);

    // 处理时间变化的函数（保存到filters以保持UI状态）
    const handleExceptionTimeChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, markExceptionAt: dates });
    };

    const handleDestWarehouseArrivedAtChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, destWarehouseArrivedAt: dates });
    };

    const handleLastDeliveryScanTimeChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, lastDeliveryScanTime: dates });
    };

    const handlePickupAtChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, receivedAtHub: dates });
    };

    const handleOrderTimeChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, orderTime: dates });
    };

    const handleDeliveredAtChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, deliveredAt: dates });
    };

    const handleHubReceiveChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, destHubArrivedAt: dates });
    };

    const handleLastTaskDateChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, deliverAt: dates });
    };

    const handleUpdatedAtChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, updatedAt: dates });
    };

    const handleReturnArrivalHubAtChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, returnArrivalHubAt: dates });
    };

    const handleCloseTimeChange = (dates: [string | null, string | null] | null) => {
        setFilters({ ...filters, closeTime: dates });
    };

    const handleCloseOperatorAccountIdChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim();
        setFilters({ 
            ...filters, 
            closeOperatorAccountId: value ? parseInt(value, 10) : undefined 
        });
    };

    // 处理 Warehouse 选择变化（清空时同时清空 sortingCodeSegment2）
    const handleWarehouseIdChange = useCallback((value: string | undefined) => {
        if (!value) {
            // 清空时，同时清空 sortingCodeSegment2 和 warehouseId
            setFilters(prev => ({
                ...prev,
                warehouseId: undefined,
                sortingCodeSegment2: undefined
            }));
        } else {
            // 选择时，只更新 warehouseId，sortingCodeSegment2 由 handleSecondSegmentChange 更新
            setFilters(prev => ({ 
                ...prev, 
                warehouseId: value
            }));
        }
    }, [setFilters]);

    // 处理第二段码选择变化
    const handleSecondSegmentChange = useCallback((secondSegment: string | undefined) => {
        // 只在选择时更新 sortingCodeSegment2，清空时由 handleWarehouseIdChange 统一处理
        if (secondSegment) {
            setFilters(prev => ({ ...prev, sortingCodeSegment2: secondSegment }));
        }
    }, [setFilters]);

    return (
        <div className="mb-6">
            {/* Base Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-500 mb-2">{t('filters.sections.baseInformation')}</div>
                <div className="bg-gray-50 rounded-md p-4">
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0 col-span-3">
                            <BatchInputFilterItem
                                label={t('central.parcels.filters.tracking')}
                                value={filters.trackingNumbers || ''}
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                                    ...filters,
                                    trackingNumbers: e.target.value || undefined
                                })}
                                spanClassName="w-[9em]"
                                placeholder={t('central.parcels.placeholders.trackingNumbers') || ''}
                                rows={2}
                            />
                        </div>
                        <div className="min-w-0">
                            <InputFilterItem
                                label={t('central.parcels.filters.order')}
                                value={filters.channelOrderNumber || ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const value = e.target.value.trim();
                                    setFilters({
                                        ...filters,
                                        channelOrderNumber: value || undefined
                                    });
                                }}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <OrderCloseClientSelectFilter
                                label={t('central.parcels.filters.client')}
                                value={filters.channelIds?.map(String)}
                                onChange={(value: string[] | undefined) => setFilters({ 
                                    ...filters, 
                                    channelIds: value?.map(v => parseInt(v))
                                })}
                                spanClassName="w-[9em]"
                                placeholder="All"
                            />
                        </div>
                        <div className="min-w-0">
                            <MultiParcelStatusSelectFilter
                                label={t('central.parcels.filters.parcelStatus')}
                                value={filters.overallStatuses}
                                onChange={(value: string[] | undefined) => setFilters({ ...filters, overallStatuses: value })}
                                spanClassName="w-[9em]"
                                placeholder="All"
                            />
                        </div>
                        <div className="min-w-0">
                            <DspSelectFilter
                                label="DSP"
                                value={filters.deliveredDspIds?.map(String)}
                                onChange={(value: string[] | undefined) => setFilters({
                                    ...filters,
                                    deliveredDspIds: value?.map(v => parseInt(v))
                                })}
                                spanClassName="w-[9em]"
                                placeholder="All"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Route Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-500 mb-2">{t('filters.sections.routeInformation')}</div>
                <div className="bg-gray-50 rounded-md p-4">
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                            <HubSelectFilter
                                label={t('central.parcels.filters.sortCode1')}
                                value={filters.sortingCodeSegment1}
                                onChange={(value, hubId) => {
                                    // 当清空 Hub 选择时，同时清空相关字段
                                    if (!value) {
                                        setSelectedHubId(undefined);
                                        setFilters(prev => ({ 
                                            ...prev, 
                                            sortingCodeSegment1: undefined,
                                            hubId: undefined,
                                            warehouseId: undefined,
                                            sortingCodeSegment2: undefined
                                        }));
                                    } else {
                                        // 选择 Hub 时，同时更新 sortingCodeSegment1 和 hubId
                                        setSelectedHubId(hubId); // 保存选中的 Hub ID，用于级联过滤
                                        setFilters(prev => ({ 
                                            ...prev, 
                                            sortingCodeSegment1: value,
                                            hubId: hubId,
                                            warehouseId: undefined, // 切换 Hub 时清空 Warehouse
                                            sortingCodeSegment2: undefined // 切换 Hub 时清空二段码
                                        }));
                                    }
                                }}
                                spanClassName="w-[9em]"
                                placeholder={t('central.parcels.placeholders.selectHub') || 'Select HUB'}
                            />
                        </div>
                        <div className="min-w-0">
                            <WarehouseSelectFilter
                                label={t('central.parcels.filters.sortCode2')}
                                value={filters.warehouseId}
                                onChange={handleWarehouseIdChange}
                                onSelectSecondSegment={handleSecondSegmentChange}
                                spanClassName="w-[9em]"
                                hubId={selectedHubId}
                                placeholder={t('central.parcels.placeholders.selectWarehouse') || 'Select Warehouse'}
                            />
                        </div>
                        <div className="min-w-0">
                            <InputFilterItem
                                label={t('central.parcels.filters.sortCode3')}
                                value={filters.sortingCodeSegment3 || ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({
                                    ...filters,
                                    sortingCodeSegment3: e.target.value || undefined
                                })}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <BatchInputFilterItem
                                label={t('central.parcels.filters.receiverZipcode')}
                                value={filters.receiverZipcodeInput || filters.receiverZipcode?.join('\n') || ''}
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                                    const value = e.target.value;
                                    // 与 trackingNumbers 一致：直接保存原始字符串，在查询时转换为数组
                                    setFilters({
                                        ...filters,
                                        receiverZipcodeInput: value || undefined
                                    });
                                }}
                                placeholder={t('central.parcels.placeholders.receiverZipcode') || 'Enter multiple zipcodes, separated by line, comma or space'}
                                spanClassName="w-[9em]"
                                rows={1}
                            />
                        </div>
                        <div className="min-w-0">
                            <MultiPickupHubSelectFilter
                                label={t('delivery.parcels.label.pickupHub')}
                                value={filters.pickupHubId}
                                onChange={(value: string[] | undefined) => setFilters({...filters, pickupHubId: value})}
                                spanClassName="w-[9em]"
                                placeholder={'All'}
                            />
                        </div>
                        <div className="min-w-0">
                            <InputFilterItem
                                label={t('central.parcels.filters.pickupSite')}
                                value={filters.cooperationWarehouseId?.toString()}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const value = e.target.value.trim();
                                    const numValue = value ? parseInt(value, 10) : undefined;
                                    setFilters({
                                        ...filters,
                                        cooperationWarehouseId: (numValue && !isNaN(numValue)) ? numValue : undefined
                                    });
                                }}
                                spanClassName="w-[9em]"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Date Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-500 mb-2">{t('filters.sections.dateInformation')}</div>
                <div className="bg-gray-50 rounded-md p-4">
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('central.parcels.filters.orderTime')}
                                value={filters.orderTime}
                                onChange={handleOrderTimeChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('central.parcels.filters.pickupTaskDate')}
                                value={filters.receivedAtHub}
                                onChange={handlePickupAtChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('central.parcels.filters.deliveredAt')}
                                value={filters.deliveredAt}
                                onChange={handleDeliveredAtChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('central.parcels.filters.hubReceiveDate')}
                                value={filters.destHubArrivedAt}
                                onChange={handleHubReceiveChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('central.parcels.filters.whsReceiveDate')}
                                value={filters.destWarehouseArrivedAt}
                                onChange={handleDestWarehouseArrivedAtChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('central.parcels.filters.lastDeliveryScanTime')}
                                value={filters.lastDeliveryScanTime}
                                onChange={handleLastDeliveryScanTimeChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('central.parcels.filters.lastTaskDate')}
                                value={filters.deliverAt}
                                onChange={handleLastTaskDateChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('delivery.parcels.label.lastUpdatedAt')}
                                value={filters.updatedAt}
                                onChange={handleUpdatedAtChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('central.parcels.filters.returnArrivalHubAt')}
                                value={filters.returnArrivalHubAt}
                                onChange={handleReturnArrivalHubAtChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('orderClose.fields.closeTime')}
                                value={filters.closeTime}
                                onChange={handleCloseTimeChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Exception Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-500 mb-2">{t('filters.sections.exceptionInformation')}</div>
                <div className="bg-gray-50 rounded-md p-4">
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('central.parcels.filters.isComplaint')}
                                options={isComplaintOptions}
                                value={filters.isComplaint?.toString() ?? undefined}
                                onChange={(value: string) => setFilters({...filters, isComplaint: value ? (value === 'true') : undefined})}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('delivery.parcels.label.podReviewStatus')}
                                options={podReviewStatusOptions}
                                value={filters.podReviewStatus ?? undefined}
                                onChange={(value: string) => setFilters({...filters, podReviewStatus: value || undefined})}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('central.parcels.filters.hasException')}
                                options={hasExceptionOptions}
                                value={filters.hasException?.toString() ?? undefined}
                                onChange={(value: string) => setFilters({
                                    ...filters, 
                                    hasException: value ? (value === 'true') : undefined
                                })}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('central.parcels.filters.lastExceptionReason')}
                                options={exceptionList.filter(ex => !ex.isInternalException).map(ex => ({
                                    value: ex.exceptionReasonCode,
                                    label: getExceptionReasonText(ex, ex.exceptionReasonCode)
                                })) || []}
                                value={filters.exceptionReasonCode ?? undefined}
                                onChange={(value: string) => setFilters({...filters, exceptionReasonCode: value || undefined})}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={t('central.parcels.filters.lastExceptionDate')}
                                value={filters.markExceptionAt}
                                onChange={handleExceptionTimeChange}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('central.parcels.filters.nextAction')}
                                options={nextActionSelectData.filter(item => item.value !== 0).map(item => ({
                                    value: item.name,
                                    label: getNextActionTextFromBackend(item.name)
                                }))}
                                mode="multiple"
                                value={filters.nextActions}
                                onChange={(value: string[] | undefined) => setFilters({...filters, nextActions: value, nextAction: undefined})}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('central.parcels.filters.exceptionStatus')}
                                options={exceptionOrderStatusOptions}
                                mode="multiple"
                                maxTagCount="responsive"
                                value={filters?.exceptionStatuses?.map(String) ?? []}
                                onChange={(values: string[]) => setFilters({
                                    ...filters,
                                    exceptionStatuses: values && values.length > 0 && values.length < exceptionOrderStatusOptions.length ? values.map(Number) : undefined
                                })}
                                spanClassName="w-[9em]"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Close Order Specific Filters */}
            <div className="mb-4">
                <div className="text-sm text-gray-500 mb-2">{t('orderClose.filters.title')}</div>
                <div className="bg-gray-50 rounded-md p-4">
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                            <NumberRangeFilterItem
                                label={t('orderClose.fields.daysFromAscan')}
                                value={filters.daysFromAscan}
                                onChange={(value) => {
                                    setFilters(prev => ({
                                        ...prev,
                                        daysFromAscan: value
                                    }));
                                }}
                                placeholder={[t('orderClose.fields.minDays'), t('orderClose.fields.maxDays')]}
                                min={0}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <NumberRangeFilterItem
                                label={t('orderClose.fields.hoursToClose')}
                                value={filters.hoursToClose}
                                onChange={(value) => {
                                    setFilters(prev => ({
                                        ...prev,
                                        hoursToClose: value
                                    }));
                                }}
                                placeholder={[t('orderClose.fields.minHours'), t('orderClose.fields.maxHours')]}
                                spanClassName="w-[12em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <InputFilterItem
                                label={t('orderClose.fields.closeOperator')}
                                value={filters.closeOperatorAccountId?.toString() || ''}
                                onChange={handleCloseOperatorAccountIdChange}
                                placeholder={t('orderClose.fields.closeOperatorPlaceholder') || '请输入账户ID'}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('orderClose.fields.hasCloseRecord')}
                                options={hasCloseRecordOptions}
                                value={filters.hasCloseRecord?.toString() ?? undefined}
                                onChange={(value: string) => setFilters({
                                    ...filters,
                                    hasCloseRecord: value ? (value === 'true') : undefined
                                })}
                                spanClassName="w-[9em]"
                            />
                        </div>
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('orderClose.fields.flowType')}
                                options={[
                                    { label: t('orderClose.flowType.FORWARD_UNCLOSED'), value: 'FORWARD_UNCLOSED' },
                                    { label: t('orderClose.flowType.REVERSE_UNCLOSED'), value: 'REVERSE_UNCLOSED' }
                                ]}
                                value={filters.flowType ?? undefined}
                                onChange={(value: string) => setFilters({
                                    ...filters,
                                    flowType: value || undefined
                                })}
                                spanClassName="w-[9em]"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderCloseFilter;
